import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";

// Resolve current user id from optional bearer token (without requiring auth).
async function tryGetUserId(): Promise<string | null> {
  try {
    const auth = getRequestHeader("authorization");
    if (!auth) return null;
    const token = auth.replace(/^Bearer\s+/i, "").trim();
    if (!token) return null;
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data } = await supabaseAdmin.auth.getUser(token);
    return data.user?.id ?? null;
  } catch {
    return null;
  }
}

const MAX_FILES = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB each
const MAX_TOTAL_BYTES = 20 * 1024 * 1024; // 20 MB total
const ALLOWED_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/plain",
]);

const attachmentSchema = z.object({
  name: z.string().trim().min(1).max(160),
  contentType: z.string().trim().min(3).max(120),
  dataBase64: z.string().min(1),
});

const submitSchema = z.object({
  full_name: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(40).optional().default(""),
  company: z.string().trim().max(160).optional().default(""),
  registration: z.string().trim().max(60).optional().default(""),
  sub_service: z.string().trim().max(120).optional().default(""),
  contact_method: z
    .enum(["Email", "Phone call", "WhatsApp"])
    .default("Email"),
  details: z.string().trim().min(5).max(4000),
  attachments: z.array(attachmentSchema).max(MAX_FILES).optional().default([]),
});

function sanitizeFilename(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 120) || "file";
}

export const submitCosecQuote = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => submitSchema.parse(input))
  .handler(async ({ data }) => {
    const userId = await tryGetUserId();
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );

    // Validate attachments size
    let totalBytes = 0;
    for (const a of data.attachments) {
      if (!ALLOWED_TYPES.has(a.contentType)) {
        throw new Error(`Unsupported file type: ${a.contentType}`);
      }
      // base64 size ≈ length * 3/4
      const sz = Math.floor((a.dataBase64.length * 3) / 4);
      if (sz > MAX_FILE_BYTES) throw new Error(`${a.name} exceeds 5 MB`);
      totalBytes += sz;
    }
    if (totalBytes > MAX_TOTAL_BYTES)
      throw new Error("Attachments exceed 20 MB total");

    // Insert quote first to get an id, then upload attachments under that id
    const { data: inserted, error } = await supabaseAdmin
      .from("cosec_quote_requests")
      .insert({
        user_id: userId,
        full_name: data.full_name,
        email: data.email,
        phone: data.phone || null,
        company: data.company || null,
        registration: data.registration || null,
        sub_service: data.sub_service || null,
        contact_method: data.contact_method,
        details: data.details,
        status: "new" as const,
      })
      .select("id")
      .single();
    if (error || !inserted) {
      console.error("[submitCosecQuote]", error);
      throw new Error("Could not save your request. Please try again.");
    }

    const reference = `COSEC-${String(inserted.id)
      .replace(/-/g, "")
      .slice(0, 8)
      .toUpperCase()}`;

    // Upload attachments
    const uploaded: Array<{
      path: string;
      name: string;
      size: number;
      content_type: string;
    }> = [];
    for (const a of data.attachments) {
      try {
        const bytes = Uint8Array.from(atob(a.dataBase64), (c) =>
          c.charCodeAt(0),
        );
        const safe = sanitizeFilename(a.name);
        const path = `${inserted.id}/${Date.now()}-${safe}`;
        const { error: upErr } = await supabaseAdmin.storage
          .from("cosec-uploads")
          .upload(path, bytes, {
            contentType: a.contentType,
            upsert: false,
          });
        if (upErr) {
          console.error("[submitCosecQuote] upload", upErr);
          continue;
        }
        uploaded.push({
          path,
          name: a.name,
          size: bytes.byteLength,
          content_type: a.contentType,
        });
      } catch (e) {
        console.error("[submitCosecQuote] file decode", e);
      }
    }

    await supabaseAdmin
      .from("cosec_quote_requests")
      .update({ attachments: uploaded as any, reference })
      .eq("id", inserted.id);

    const submittedAt = new Date().toISOString();
    const serviceLabel = data.sub_service || "Company Secretarial";
    const origin = process.env.SITE_URL ?? "https://nkadimengdannylcs.com";
    const checklistUrl = `${origin}/downloads/company-secretarial-documents-checklist.pdf`;

    // SMS notify (best-effort)
    let adminSmsOk = false;
    let clientSmsOk = false;
    try {
      const { sendSms, ADMIN_PHONE } = await import("@/lib/sms.server");
      const adminMsg =
        `New Company Secretarial quote — ${data.full_name}\n` +
        `Ref: ${reference}\n` +
        `Service: ${serviceLabel}\n` +
        `Contact: ${data.contact_method}\n` +
        (data.phone ? `Phone: ${data.phone}\n` : "") +
        `Email: ${data.email}\n` +
        (uploaded.length ? `Attachments: ${uploaded.length}\n` : "") +
        `Msg: ${data.details.slice(0, 160)}`;
      const adminR = await sendSms(ADMIN_PHONE, adminMsg);
      adminSmsOk = adminR.ok;

      if (data.phone) {
        const clientMsg =
          `Hi ${data.full_name.split(" ")[0]}, we've received your Company ` +
          `Secretarial quote request (${serviceLabel}, Ref ${reference}). ` +
          `We'll respond by ${data.contact_method} within 1 business day. ` +
          `Documents checklist: ${checklistUrl} · Tel: 071 276 6198`;
        const clientR = await sendSms(data.phone, clientMsg);
        clientSmsOk = clientR.ok;
      } else {
        clientSmsOk = true; // nothing to send
      }
    } catch (e) {
      console.error("[submitCosecQuote] sms failed", e);
    }


    // Email confirmations (reuses enquiry-received template)
    let adminEmailOk = false;
    let clientEmailOk = false;
    try {
      const { sendSystemEmail } = await import(
        "@/lib/email/system-send.server"
      );

      // Try to load the checklist PDF and attach it to the client email.
      let checklistAttachment:
        | { filename: string; contentBase64: string; contentType: string }
        | null = null;
      try {
        const { readFile } = await import("node:fs/promises");
        const { join } = await import("node:path");
        const buf = await readFile(
          join(process.cwd(), "public", "downloads", "company-secretarial-documents-checklist.pdf"),
        );
        checklistAttachment = {
          filename: "company-secretarial-documents-checklist.pdf",
          contentBase64: Buffer.from(buf).toString("base64"),
          contentType: "application/pdf",
        };
      } catch (e) {
        console.warn("[submitCosecQuote] checklist attachment unavailable", e);
      }

      const fullMessage = [
        `Service: ${serviceLabel}`,
        `Contact method: ${data.contact_method}`,
        data.company ? `Company: ${data.company}` : null,
        data.registration ? `CIPC reg: ${data.registration}` : null,
        uploaded.length
          ? `Attachments (${uploaded.length}): ${uploaded
              .map((u) => u.name)
              .join(", ")}`
          : null,
        ``,
        data.details,
      ]
        .filter(Boolean)
        .join("\n");
      const baseData = {
        fullName: data.full_name,
        email: data.email,
        phone: data.phone || "",
        service: `Company Secretarial — ${serviceLabel}`,
        message: fullMessage,
        reference,
        submittedAt,
      };
      const [adminE, clientE] = await Promise.all([
        sendSystemEmail({
          templateName: "enquiry-received",
          recipientEmail: "nkadimengdanny7@gmail.com",
          templateData: { ...baseData, audience: "admin" },
          idempotencyKey: `cosec-admin-${inserted.id}`,
        }),
        sendSystemEmail({
          templateName: "enquiry-received",
          recipientEmail: data.email,
          templateData: {
            ...baseData,
            audience: "client",
            downloadUrl: checklistUrl,
            checklistUrl,
            attachmentNote: checklistAttachment
              ? "Your Company Secretarial documents checklist is attached to this email."
              : `Download the checklist: ${checklistUrl}`,
          },
          idempotencyKey: `cosec-client-${inserted.id}`,
          attachments: checklistAttachment ? [checklistAttachment] : undefined,
        }),
      ]);

      adminEmailOk = adminE.ok;
      clientEmailOk = clientE.ok;
    } catch (e) {
      console.error("[submitCosecQuote] email failed", e);
    }

    await supabaseAdmin
      .from("cosec_quote_requests")
      .update({
        admin_sms_status: adminSmsOk ? "sent" : "failed",
        client_sms_status: data.phone
          ? clientSmsOk
            ? "sent"
            : "failed"
          : "skipped",
        admin_email_status: adminEmailOk ? "sent" : "failed",
        client_email_status: clientEmailOk ? "sent" : "failed",
      })
      .eq("id", inserted.id);

    return {
      ok: true as const,
      id: inserted.id,
      reference,
      uploaded: uploaded.length,
    };
  });

/* ----------------------- Admin functions ---------------------- */

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./_admin-guard";

export const adminListCosecQuotes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("cosec_quote_requests")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return { rows: data ?? [] };
  });

const updateSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["new", "contacted", "quoted", "completed"]).optional(),
  admin_notes: z.string().max(4000).optional(),
});

const STATUS_LABELS: Record<string, string> = {
  new: "received",
  contacted: "in progress — we've made contact",
  quoted: "quoted — awaiting your response",
  completed: "completed",
};

export const adminUpdateCosecQuote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => updateSchema.parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );

    // Read prior row so we can detect a real status change
    const { data: prior } = await supabaseAdmin
      .from("cosec_quote_requests")
      .select(
        "id, status, full_name, email, phone, reference, sub_service, contact_method",
      )
      .eq("id", data.id)
      .maybeSingle();

    const patch: any = {};
    const nowIso = new Date().toISOString();
    if (data.status) {
      patch.status = data.status;
      if (data.status === "contacted") patch.contacted_at = nowIso;
      if (data.status === "quoted") patch.quoted_at = nowIso;
      if (data.status === "completed") patch.completed_at = nowIso;
    }
    if (typeof data.admin_notes === "string")
      patch.admin_notes = data.admin_notes;
    const { error } = await supabaseAdmin
      .from("cosec_quote_requests")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);

    // Notify client when the status actually changes
    const statusChanged =
      !!data.status && !!prior && (prior as any).status !== data.status;
    if (statusChanged && prior) {
      const p = prior as any;
      const label = STATUS_LABELS[data.status!] ?? data.status!;
      const service = p.sub_service || "Company Secretarial";
      let emailStatus = "skipped";
      let smsStatus = "skipped";

      try {
        const { sendSystemEmail } = await import(
          "@/lib/email/system-send.server"
        );
        const r = await sendSystemEmail({
          templateName: "enquiry-received",
          recipientEmail: p.email,
          templateData: {
            audience: "client",
            fullName: p.full_name,
            service: `Company Secretarial — ${service}`,
            reference: p.reference,
            submittedAt: new Date().toLocaleString("en-ZA"),
            message: `Status update: your request is now ${label.toUpperCase()}. We'll follow up by ${p.contact_method || "email"}.`,
          },
          idempotencyKey: `cosec-status-${data.id}-${data.status}`,
        });
        emailStatus = r.ok ? "sent" : "failed";
      } catch (e) {
        console.error("[adminUpdateCosecQuote] email", e);
        emailStatus = "failed";
      }

      if (p.phone) {
        try {
          const { sendSms } = await import("@/lib/sms.server");
          const first = String(p.full_name || "").split(" ")[0] || "there";
          const r = await sendSms(
            p.phone,
            `Hi ${first}, your Company Secretarial request (Ref ${p.reference}) is now ${label.toUpperCase()}. NDLCS · 071 276 6198`,
          );
          smsStatus = r.ok ? "sent" : "failed";
        } catch (e) {
          console.error("[adminUpdateCosecQuote] sms", e);
          smsStatus = "failed";
        }
      }

      await supabaseAdmin
        .from("cosec_quote_requests")
        .update({
          last_status_notified_at: nowIso,
          last_status_email_status: emailStatus,
          last_status_sms_status: smsStatus,
        })
        .eq("id", data.id);
    }

    return { ok: true as const, notified: statusChanged };
  });


const signSchema = z.object({ path: z.string().min(3).max(500) });

export const adminSignCosecAttachment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => signSchema.parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: signed, error } = await supabaseAdmin.storage
      .from("cosec-uploads")
      .createSignedUrl(data.path, 60 * 15);
    if (error || !signed) throw new Error(error?.message || "sign failed");
    return { url: signed.signedUrl };
  });
